import {faFilePdf} from '@fortawesome/pro-regular-svg-icons';
import {faChartColumn} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {AlertFallback} from '@rdcomponents/alert-fallback/alert-fallback';
import {PageHeaderWithLicenseTag} from '@rdcomponents/header/header-with-license-tag/page-header-with-license-tag';
import {LicensePageCard} from '@rdcomponents/license-page-card/license-page-card';
import {Meta} from '@rdcomponents/meta/meta';
import {PageCardDivider} from '@rdcomponents/page-card-divider/page-card-divider';
import {UsetifulNameProductEnum} from '@rdcomponents/usetiful/usetiful-const';
import {Alert, Button, Col, Row} from 'antd';
import {useRef} from 'react';
import {useReactToPrint} from 'react-to-print';

import {appRoute} from '../../../app-route';
import {BreadCrumbs} from '../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../layout/page/page';
import {MainPageContainer} from '../../../layout/page-card/main-page-card';
import {PageCard} from '../../../layout/page-card/page-card';
import {Spinner} from '../../../layout/spinner/spinner';
import {useLicenses} from '../../../provider/license/license-hook';
import {useLocale} from '../../../provider/locale/locale-hook';
import {Locale} from '../../../provider/locale/localization';
import {
    useOnlinePresenceFormattedLabels,
    useOnlinePresencePriorityPlacement,
    useOnlinePresenceV2Filter,
    useOnlinePresenceV2YandexDeepClicks,
    useOnlinePresenceV2YandexStats,
} from '../../../service/online-presence/online-presence-hook';
import {FeaturesEnum} from '../../../service/user/user-type';
import {ProvidersEnum} from '../../../util/type';

import {ImpressionsDynamic} from './impressions-dynamic/impressions-dynamic';
import {OnlinePresenceFilterV2} from './online-presence-filter-v2/online-presence-filter-v2';
import {OnlinePresenceDashboardPdfContent} from './pdf-content/online-presence-dashboard-pdf-content';
import {PrimaryIndicators} from './primary-indicators/primary-indicators';
import {TargetActionsDynamics} from './target-actions-dynamics/target-actions-dynamics';
import {
    yandexTargetActionsDetailsChartKeys,
    yandexTargetActionsMainChartKeys,
} from './target-actions-dynamics/target-actions-dynamics-helper';
import * as styles from './online-presence.scss';

// eslint-disable-next-line complexity
export function OnlinePresenceYandex(): JSX.Element {
    const {getLocalizedString} = useLocale();

    const {filter, dispatchFilter, isCompareMode, formattedPeriods} = useOnlinePresenceV2Filter();

    const {result, isInProgress, processError} = useOnlinePresenceV2YandexStats(filter);
    const {result: yandexDeepClicksResult, isInProgress: yandexDeepClicksInProgress} =
        useOnlinePresenceV2YandexDeepClicks(filter);
    const {labels, comparedLabels} = useOnlinePresenceFormattedLabels(result?.labels, result?.comparedLabels);
    const {licenses} = useLicenses();
    const license = licenses[FeaturesEnum.onlinePresenceAnalytics];

    const {data, isLoading} = useOnlinePresencePriorityPlacement();

    const isLicenseExpired = !license?.isActive;

    const pdfContentRef = useRef<HTMLDivElement | null>(null);

    const handlePrintClick = useReactToPrint({
        content: () => pdfContentRef.current,
    });

    return (
        <Page renderContainer={(pageContent) => <MainPageContainer>{pageContent}</MainPageContainer>}>
            <PageCard>
                <Meta title={getLocalizedString('ONLINE_PRESENCE_ANALYTICS__BREAD_CRUMBS')} />
                <BreadCrumbs
                    list={[
                        {
                            path: appRoute.onlinePresenceDashboard.path,
                            titleLangKey: 'ONLINE_PRESENCE_ANALYTICS__BREAD_CRUMBS',
                        },
                    ]}
                />

                <PageHeaderWithLicenseTag
                    className={styles.OnlinePresence_header}
                    headerLangKey="ONLINE_PRESENCE_ANALYTICS__CATALOG_TITLE"
                    headerValueMap={{
                        catalogName: (
                            <span key="catalogName">
                                <Locale stringKey="ONLINE_PRESENCE__CATALOG__YANDEX" />
                            </span>
                        ),
                    }}
                    licenseActive={license?.isActive}
                    productName={UsetifulNameProductEnum.ONLINE_PRESENCE__YANDEX}
                    trialLicense={license?.isTrial}
                >
                    <Button
                        disabled={
                            !result ||
                            isInProgress ||
                            isLicenseExpired ||
                            !yandexDeepClicksResult ||
                            yandexDeepClicksInProgress
                        }
                        icon={<FontAwesomeIcon icon={faFilePdf} />}
                        onClick={handlePrintClick}
                    >
                        <Locale stringKey="ONLINE_PRESENCE_ANALYTICS__DOWNLOAD_PDF" />
                    </Button>
                </PageHeaderWithLicenseTag>

                <PageCardDivider />

                <OnlinePresenceFilterV2
                    dispatchFilter={dispatchFilter}
                    filter={filter}
                    isDisabled={isInProgress || isLicenseExpired}
                />
                {!isLoading && data?.[ProvidersEnum.yandex] !== 0 && (
                    <Alert
                        className={styles.OnlinePresence_alert}
                        closable
                        description={getLocalizedString('ONLINE_PRESENCE_ANALYTICS__WARNING_PRIORITY_PLACEMENT_YANDEX')}
                        message={getLocalizedString('ONLINE_PRESENCE_ANALYTICS__WARNING')}
                        showIcon
                        type="warning"
                    />
                )}
            </PageCard>

            {processError && <AlertFallback />}

            {isInProgress && <Spinner />}

            <LicensePageCard
                icon={<FontAwesomeIcon icon={faChartColumn} />}
                license={license}
                productLandKey="CATEGORY_NAME__ONLINE_PRESENCE"
            />

            {!isLicenseExpired && (
                <>
                    {result && (
                        <Row gutter={[24, 24]}>
                            <Col className={styles.OnlinePresence_column} xl={12} xs={24}>
                                <PrimaryIndicators
                                    data={result.base}
                                    formattedPeriods={formattedPeriods}
                                    isCompareMode={isCompareMode}
                                />

                                <ImpressionsDynamic
                                    comparePeriodData={result.impressionsDynamic.compared}
                                    comparedLabels={comparedLabels}
                                    isCompareMode={isCompareMode}
                                    labels={labels}
                                    mainPeriodData={result.impressionsDynamic.current}
                                />
                            </Col>
                            <Col className={styles.OnlinePresence_column} xl={12} xs={24}>
                                <TargetActionsDynamics
                                    comparedLabels={comparedLabels}
                                    data={result.actionsDynamic}
                                    detailsChartAllowedKeys={yandexTargetActionsDetailsChartKeys}
                                    formattedPeriods={formattedPeriods}
                                    isCompareMode={isCompareMode}
                                    labels={labels}
                                    mainChartAllowedKeys={yandexTargetActionsMainChartKeys}
                                />

                                {yandexDeepClicksResult && (
                                    <TargetActionsDynamics
                                        comparedLabels={comparedLabels}
                                        data={yandexDeepClicksResult.actionsDynamic}
                                        emptyLangKey="ONLINE_PRESENCE_ANALYTICS__INVOLVEMENT__EMPTY"
                                        formattedPeriods={formattedPeriods}
                                        isCompareMode={isCompareMode}
                                        labels={labels}
                                        titleLangKey="ONLINE_PRESENCE_ANALYTICS__INVOLVEMENT__TITLE"
                                    />
                                )}
                            </Col>
                        </Row>
                    )}

                    {result && (
                        <div className={styles.OnlinePresence_pdfContent}>
                            <OnlinePresenceDashboardPdfContent
                                extendedMetrics={{
                                    emptyLangKey: 'ONLINE_PRESENCE_ANALYTICS__INVOLVEMENT__EMPTY',
                                    titleLangKey: 'ONLINE_PRESENCE_ANALYTICS__INVOLVEMENT__TITLE',
                                    data: yandexDeepClicksResult?.actionsDynamic,
                                }}
                                filter={filter}
                                isCompareMode={isCompareMode}
                                overrideTargetActionsDynamics={
                                    <TargetActionsDynamics
                                        comparedLabels={comparedLabels}
                                        data={result.actionsDynamic}
                                        detailsChartAllowedKeys={yandexTargetActionsDetailsChartKeys}
                                        formattedPeriods={formattedPeriods}
                                        isCompareMode={isCompareMode}
                                        labels={labels}
                                        mainChartAllowedKeys={yandexTargetActionsMainChartKeys}
                                        staticChart
                                    />
                                }
                                ref={pdfContentRef}
                                result={result}
                            />
                        </div>
                    )}
                </>
            )}
        </Page>
    );
}
